import React, { useEffect, useState } from 'react';
import { Box, Grid, Paper, Typography, Button, Skeleton, useTheme, alpha } from '@mui/material';
import HourglassTopIcon from '@mui/icons-material/HourglassTopRounded';
import AssignmentIcon from '@mui/icons-material/AssignmentRounded';
import AddCircleIcon from '@mui/icons-material/AddCircleRounded';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  getReviewDashboardCountForTeacher, getAssignmentsList, getTeacherUser, getBranchId,
} from '../api/teacherApi';
import StatCard from '../components/StatCard';
import { tokens } from '../theme/tokens';
import { staggerContainer, fadeInUp } from '../utils/motion';

const MotionPaper = motion(Paper);

const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
};

const Dashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const user = getTeacherUser();
  const [pendingReviews, setPendingReviews] = useState(null);
  const [totalAssignments, setTotalAssignments] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const [reviewRes, listRes] = await Promise.allSettled([
          getReviewDashboardCountForTeacher(),
          getAssignmentsList({ branch_id: getBranchId() }),
        ]);

        if (reviewRes.status === 'fulfilled') {
          // NOTE: field name is a guess (pending_count / pending / count) —
          // adjust once the real response shape for this endpoint is confirmed.
          const d = reviewRes.value.data;
          setPendingReviews(d?.pending_count ?? d?.pending ?? d?.count ?? '—');
        }
        if (listRes.status === 'fulfilled') {
          const data = listRes.value.data;
          const list = Array.isArray(data) ? data : data?.results || [];
          setTotalAssignments(list.length);
        }
      } catch (err) {
        console.error('Failed to load teacher dashboard', err);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  const firstName = (user?.employee_name || 'Teacher').split(' ')[0];

  return (
    <Box component={motion.div} variants={staggerContainer} initial="hidden" animate="show">
      <MotionPaper
        variants={fadeInUp}
        elevation={0}
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: '22px',
          p: { xs: 3, sm: 4 },
          mb: 3,
          color: '#fff',
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${tokens.violetDeep} 100%)`,
        }}
      >
        <Box
          sx={{
            position: 'absolute', width: 260, height: 260, borderRadius: '50%',
            background: alpha('#fff', 0.12), top: -110, right: -60,
            animation: 'floatSlow 9s ease-in-out infinite',
          }}
        />
        <Box sx={{ position: 'relative', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Typography variant="body2" sx={{ opacity: 0.85, mb: 0.5, fontWeight: 600 }}>
              {getGreeting()}
            </Typography>
            <Typography variant="h4" sx={{ fontFamily: 'var(--font-display)', fontWeight: 800, mb: 1 }}>
              {firstName} 👋
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.85, maxWidth: 420 }}>
              {user?.branch_name ? `${user.branch_name}` : 'Welcome back to your teaching space.'}
              {user?.organization_name ? ` — ${user.organization_name}` : ''}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddCircleIcon />}
            onClick={() => navigate('/teacher/assignments')}
            sx={{
              alignSelf: 'flex-start',
              backgroundColor: 'rgba(255,255,255,0.16)',
              backdropFilter: 'blur(6px)',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.26)' },
            }}
          >
            Create assignment
          </Button>
        </Box>
      </MotionPaper>

      <Grid container spacing={2}>
        {loading ? (
          [0, 1].map((i) => (
            <Grid item xs={12} sm={6} key={i}>
              <Skeleton variant="rounded" height={92} sx={{ borderRadius: '18px' }} />
            </Grid>
          ))
        ) : (
          <>
            <Grid item xs={12} sm={6}>
              <StatCard
                icon={<HourglassTopIcon />}
                label="Pending reviews"
                value={pendingReviews ?? '—'}
                color={tokens.amber}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StatCard
                icon={<AssignmentIcon />}
                label="Assignments created"
                value={totalAssignments ?? '—'}
                color={theme.palette.primary.main}
              />
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
};

export default Dashboard;
